import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { otherDataAC } from "./store/action-creators";
import { StateLoader } from "./utils/services/stateLoader";
import RequireAuth from "./utils/RequireAuth";
import { initWeb3Auth } from './utils/services/web3auth';
import { useChain, useWalletClient } from '@cosmos-kit/react';
import { changeStore } from './store/useStore';

import {  getAltAddress, isLogin, proceedLogin } from './utils/services/auth';


import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import "swiper/css";
import { notify } from './utils/services/notification';
import { useSyncProviders } from './utils/services/evm';
import History from './utils/services/GlobalNavigation/navigationHistory';


// Lazily load the components
import { Statistics } from './pages/adminPanel';
import { ClaimsPage } from './pages';
const Landingpage = lazy(() => import("./pages/landing-page/Landingpage"));
const NoPage = lazy(() => import("./pages/NoPage/NoPage"));
const FilePreview = lazy(() => import("./pages/filePreview/FilePreview"));
const AdminLayout = lazy(() => import("./pages/adminPanel/AdminLayout/AdminLayout"));
const Myspace = lazy(() => import("./pages/adminPanel/Myspace/Myspace"));
const Apikey = lazy(() => import("./pages/adminPanel/ApiKey/Apikey"));
const TopUp = lazy(() => import("./pages/adminPanel/TopUp/TopUp"));
const AccessControl = lazy(() => import("./pages/adminPanel/AccessControl/AccessControl"));
const Migration = lazy(() => import("./pages/adminPanel/Migration/Migration"));
const ViewOrder = lazy(() => import("./pages/adminPanel/ViewOrder/ViewOrder"));
const Profile = lazy(() => import("./pages/adminPanel/ProfileLayout/Profile/Profile"));
const OverView = lazy(() => import("./pages/adminPanel/ProfileLayout/Overview/OverView"));
const Billing = lazy(() => import("./pages/adminPanel/ProfileLayout/Billing/Billing"));
const Transactions = lazy(() => import("./pages/adminPanel/ProfileLayout/Transactions/Transactions"));
const Subscriptions = lazy(() => import("./pages/adminPanel/ProfileLayout/Subscriptions/Subscriptions"));
const Notifications = lazy(() => import("./pages/adminPanel/ProfileLayout/Notifications/Notifications"));
const Prefrences = lazy(() => import("./pages/adminPanel/ProfileLayout/Prefrences/Prefrences"));
const Refer = lazy(() => import("./pages/adminPanel/ProfileLayout/Refer/Refer"));
const Security = lazy(() => import("./pages/adminPanel/ProfileLayout/Security/Security"));
const MailVerification = lazy(() => import("./pages/MailVerification/MailVerification"));
const ProfileLayout = lazy(() => import("./pages/adminPanel/ProfileLayout/ProfileLayout"));
const AdminSection = lazy(() => import("./pages/adminPanel/AdminSection/AdminSection"));

function App() {
  const dispatch = useDispatch();
  // metamask providers initiation
  useSyncProviders()
  const _otherData = bindActionCreators(otherDataAC, dispatch);
  const [isLoaded, setIsLoaded] = useState(false);
  const stateLoader = new StateLoader();
  const store = useSelector((store: any) => store);
  const cosmoskitDialog = useChain("cosmoshub");
  const { client } = useWalletClient();
  const [currentLocation, setCurrentLocation] = useState<string>(window.location.pathname);


  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentLocation(window.location.pathname);
      changeStore("setData", { isOlderThanThirtyDays:false });
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);


  useEffect(() => {
    let componentMounted = true;

    if (componentMounted && client) {


      (async () => {
        try {

          const chainId = "coreum-mainnet-1";
          const address = (await (client as any)?.getAccount(chainId)).address;
          if (cosmoskitDialog.status === 'Connected') {
            cosmoskitDialog.closeView();
            const redirect = currentLocation.includes('viewFile') ? currentLocation : '/dashboard';
            if (client) {
              client.enable?.([chainId + ""]);
              changeStore("setProvider", { cosmoskitClient: client });
              changeStore("setProvider", { cosmoskitDisconnect: cosmoskitDialog?.disconnect });
            }
            if (!(getAltAddress() === address)) {
              await proceedLogin("Coreum", undefined, redirect, () => { }, cosmoskitDialog);
            }
          }
        } catch (error) {
          notify(`Error: ${error}`, 'error');
          console.error('Error inside cosmoskitDialog - APP.ts', error)
        }
      })()
    }
    return () => {
      componentMounted = false;
    };
  }, [cosmoskitDialog.status])

  useEffect(() => {
    let componentMounted = true;
    if (componentMounted) {
      initWeb3Auth();
      const isMobile = window.matchMedia("only screen and (max-width: 600px)").matches;
      _otherData.setOtherData({ isMobile: isMobile });
      _otherData.setOtherData({ sidebarClosed: isMobile ? true : false });
      (async () => {
        setIsLoaded(true);
      })();
    }
    return () => {
      componentMounted = false;
    };
  }, []);

  useEffect(() => {
    let componentMounted = true;
    if (componentMounted) {
      const obj: { [key: string]: any } = { ...store };
      // console.log('Change Store',store);

      stateLoader.saveState(obj);  
      // checking if user is older than thirty day & unpaid user - redirect to payment page and show notification
      if(isLogin() && store?.otherData?.isOlderThanThirtyDays && store.balance.dataLimit === 1073741824 && store.balance.dataUsed > 0 && window.location.pathname !== '/dashboard/topup'){
        notify('Trial Period Over | Purchase Plan To Continue Using Lighthouse','error')
        History.navigate('/dashboard/topup');
      }    
    }

    return () => {
      componentMounted = false;
    };
  }, [store]);

  return isLoaded ? (

    <Suspense fallback={<></>}>
      <Routes>
        <Route path="/" element={<Landingpage />} />
        <Route path="/claims" element={<ClaimsPage />} />
        <Route
          path="/dashboard"
          element={
            <RequireAuth redirectTo="/" isAdmin={false}>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Myspace />} />
          <Route path="migration" element={<Migration />} />
          <Route path="migration/:id" element={<ViewOrder />} />
          <Route path="topup" element={<TopUp />} />
          <Route path="topup/:paymentStatus" element={<TopUp />} />
          <Route path="apikey" element={<Apikey />} />
          <Route path="profile" element={<ProfileLayout />}>
            <Route index element={<Profile />} />
            <Route path="overview" element={<OverView />} />
            <Route path="billings" element={<Billing />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="prefrences" element={<Prefrences />} />
            <Route path="refer" element={<Refer />} />
            <Route path="security" element={<Security />} />
            <Route path="stats" element={<Statistics />} />
          </Route>
          <Route path="accessControl/:cid" element={<AccessControl />} />
          <Route path="admin" element={
            <RequireAuth redirectTo="/dashboard" isAdmin={true}>
            < AdminSection/>
          </RequireAuth>
        } />
        </Route>
        <Route path="viewFile/:id" element={<FilePreview />} />
        <Route path="*" element={<NoPage />} />
        <Route path="verify" element={<MailVerification />} />
      </Routes>
    </Suspense>

  ) : null;
}

export default App;
